import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';

const ProductsPreviewSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 340;
    el.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  return (
    <section id="produtos" ref={sectionRef} className="relative py-20 md:py-28">
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-10 flex flex-col gap-4 md:mb-14 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="reveal mb-3 inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.3em] text-secondary-500 md:text-sm">
                <span className="h-px w-8 bg-secondary-500/60" />
                Nossos Produtos
              </span>
              <h2 className="reveal reveal-delay-1 font-display text-3xl leading-tight text-foreground-950 md:text-5xl">
                Feitos com <span className="italic text-accent-400">cuidado</span>
              </h2>
            </div>

            <div className="reveal reveal-delay-2 flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                aria-label="Rolar para a esquerda"
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all cursor-pointer ${
                  canScrollLeft
                    ? 'border-background-300 text-foreground-600 hover:border-secondary-500 hover:text-secondary-400'
                    : 'pointer-events-none border-background-200 text-foreground-100/30'
                }`}
              >
                <i className="ri-arrow-left-line" />
              </button>
              <button
                onClick={() => scroll('right')}
                aria-label="Rolar para a direita"
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all cursor-pointer ${
                  canScrollRight
                    ? 'border-background-300 text-foreground-600 hover:border-secondary-500 hover:text-secondary-400'
                    : 'pointer-events-none border-background-200 text-foreground-100/30'
                }`}
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>

          {/* Illustrative note */}
          <p className="reveal reveal-delay-2 mb-6 flex items-center gap-2 font-label text-xs text-foreground-400 md:mb-8 md:text-sm">
            <i className="ri-information-line text-sm" />
            Imagens meramente ilustrativas. O produto pode variar em aparência.
          </p>

          {/* Horizontal scroll */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="scrollbar-hide -mx-2 flex gap-5 overflow-x-auto px-2 pb-4"
          >
            {products.slice(0, 4).map((product, index) => (
              <div
                key={product.id}
                className="reveal group w-[280px] flex-shrink-0 sm:w-[300px]"
                style={{ transitionDelay: `${index * 0.1}s` }}
                data-product-shop
              >
                <div className="relative h-[340px] overflow-hidden rounded-3xl border border-background-200/70 bg-background-100 transition-colors duration-500 hover:border-secondary-500/40 sm:h-[380px]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/30 to-transparent" />

                  {/* Tags */}
                  <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                    {product.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-secondary-500/25 bg-background-50/60 px-3 py-1 font-label text-[11px] text-foreground-900 backdrop-blur-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="mb-1 font-heading text-lg font-semibold text-foreground-950">
                      {product.name}
                    </h3>
                    <p className="mb-3 font-body text-sm text-foreground-400 line-clamp-2">
                      {product.shortDescription}
                    </p>
                    <Link
                      to="/cardapio"
                      className="inline-flex items-center gap-1.5 font-label text-xs text-secondary-400 transition-colors hover:text-secondary-300"
                    >
                      <span>Saiba mais</span>
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {/* Cardápio card */}
            <div className="reveal w-[280px] flex-shrink-0 sm:w-[300px]">
              <Link
                to="/cardapio"
                className="group flex h-[340px] flex-col items-center justify-center overflow-hidden rounded-3xl border border-secondary-500/30 bg-background-100 p-6 text-center transition-colors duration-500 hover:border-secondary-500 sm:h-[380px]"
              >
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-secondary-500/40 bg-secondary-500/10 transition-transform duration-500 group-hover:scale-110">
                  <i className="ri-restaurant-2-line text-2xl text-secondary-500" />
                </div>
                <h3 className="mb-2 font-heading text-xl font-semibold text-foreground-900">
                  Ver Cardápio Completo
                </h3>
                <p className="mb-4 font-body text-sm text-foreground-500">
                  Conheça todos os nossos sabores e opções
                </p>
                <span className="inline-flex items-center gap-1.5 font-label text-sm text-accent-400">
                  <span>Explorar</span>
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsPreviewSection;